// DistanceForm.js
import React, { useState, useEffect, useRef } from "react";
import Flatpickr from "react-flatpickr";
import "flatpickr/dist/themes/material_blue.css";
import axios from "axios";
import { useForm } from "react-hook-form";
import { useJsApiLoader } from "@react-google-maps/api";

const libraries = ["places"];
const MAX_STOPS = 5;

const timeSlots = [];
for (let h = 0; h < 24; h++) {
  for (let m = 0; m < 60; m += 10) {
    const raw = `${h.toString().padStart(2, "0")}:${m
      .toString()
      .padStart(2, "0")}`;
    let hour = h % 12 === 0 ? 12 : h % 12;
    let ampm = h < 12 ? "AM" : "PM";
    timeSlots.push({
      raw,
      label: `${hour}:${m.toString().padStart(2, "0")} ${ampm}`,
    });
  }
}

function getLocalYMD(date) {
  if (!date) return "";
  if (typeof date === "string" && /^\d{4}-\d{2}-\d{2}$/.test(date)) return date;
  const d = new Date(date);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function getTodayDateString() {
  return getLocalYMD(new Date());
}

function toYMD(date) {
  return getLocalYMD(date);
}

function toHM(time) {
  if (!time) return "";
  if (typeof time === "string" && /^\d{2}:\d{2}/.test(time)) return time;
  const d = new Date(time);
  return d.toTimeString().slice(0, 5);
}

function getIconForPlaceType(main, address) {
  const txt = (main + " " + address).toLowerCase();
  if (txt.includes("airport") || txt.includes("terminal")) return "fa-plane";
  if (txt.includes("station") || txt.includes("train")) return "fa-train-subway";
  if (txt.includes("bus")) return "fa-bus";
  if (txt.includes("hotel") || txt.includes("motel") || txt.includes("inn"))
    return "fa-hotel";
  if (
    txt.includes("restaurant") ||
    txt.includes("cafe") ||
    txt.includes("bar") ||
    txt.includes("bistro")
  )
    return "fa-utensils";
  if (
    txt.includes("hospital") ||
    txt.includes("clinic") ||
    txt.includes("medical center")
  )
    return "fa-hospital";
  if (
    txt.includes("school") ||
    txt.includes("college") ||
    txt.includes("university")
  )
    return "fa-school";
  if (
    txt.includes("park") ||
    txt.includes("garden") ||
    txt.includes("reserve")
  )
    return "fa-tree";
  if (txt.includes("beach")) return "fa-umbrella-beach";
  if (
    txt.includes("mall") ||
    txt.includes("shopping") ||
    txt.includes("center") ||
    txt.includes("centre")
  )
    return "fa-cart-shopping";
  if (
    txt.includes("government") ||
    txt.includes("council") ||
    txt.includes("parliament")
  )
    return "fa-landmark";
  return "fa-location-dot";
}

const DistanceForm = ({ isReturnBooking = false, context }) => {
  const [localLoading, setLocalLoading] = useState(false);
  const { register } = useForm();
  const [pickupLocation, setPickupLocation] = useState("");
  const [dropoffLocation, setDropoffLocation] = useState("");
  const [stops, setStops] = useState([]);
  const [stopFee, setStopFee] = useState(0);

  const [focusedField, setFocusedField] = useState(null);
  const [showTimePicker, setShowTimePicker] = useState(false);

  const [date, setDate] = useState(getTodayDateString());
  const [time, setTime] = useState("");

  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_API_KEY,
    libraries,
  });
  const mapRef = useRef(null);
  const [mapLoaded, setMapLoaded] = useState(false);
  const directionsRendererRef = useRef(null);

  const autocompleteServiceRef = useRef(null);
  const [autocompleteInput, setAutocompleteInput] = useState("");
  const [autocompleteResults, setAutocompleteResults] = useState([]);
  const [autocompleteActive, setAutocompleteActive] = useState(false);

  // --- Prefill + auto-submit from embed query params ---
  const autoSubmitRef = useRef(false);
  useEffect(() => {
    if (!isLoaded || autoSubmitRef.current) return;
    const sp = new URLSearchParams(window.location.search);
    if (!sp.get("prefill")) return;

    const get = (k) => (sp.get(k) || "").trim();
    const prePickup = get("pickup");
    const preDropoff = get("dropoff");
    const preStops = (get("stops") ? get("stops").split("|") : [])
      .map((s) => s.trim())
      .filter(Boolean)
      .slice(0, MAX_STOPS);
    const preDate = get("date");
    const preTime = get("time");
    const prePax = Number.parseInt(get("pax") || "1", 10);
    const preBags = Number.parseInt(get("bags") || "0", 10);

    if (prePickup) setPickupLocation(prePickup);
    if (preDropoff) setDropoffLocation(preDropoff);
    if (preStops.length) setStops(preStops);
    if (preDate) setDate(getLocalYMD(preDate));
    if (preTime) setTime(toHM(preTime));

    if (context?.setPersonalDetails) {
      context.setPersonalDetails((prev) => ({
        ...prev,
        numberOfPassengers: Number.isFinite(prePax) ? prePax : prev.numberOfPassengers,
        numberOfLuggage: Number.isFinite(preBags) ? preBags : prev.numberOfLuggage,
      }));
    }

    autoSubmitRef.current = true;

    setTimeout(() => {
      try { onSubmit(); } catch {}
      try {
        const { pathname, hash } = window.location;
        window.history.replaceState({}, "", pathname + (hash || ""));
      } catch {}
    }, 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoaded]);
  // --- End prefill patch ---

  useEffect(() => {
    if (isLoaded && !autocompleteServiceRef.current) {
      autocompleteServiceRef.current =
        new window.google.maps.places.AutocompleteService();
    }
  }, [isLoaded]);

  useEffect(() => {
    if (focusedField === "pickup") setAutocompleteInput(pickupLocation);
    else if (focusedField === "dropoff") setAutocompleteInput(dropoffLocation);
    else if (focusedField && focusedField.startsWith("stop-")) {
      const idx = parseInt(focusedField.replace("stop-", ""));
      setAutocompleteInput(stops[idx] || "");
    }
    if (focusedField) {
      setAutocompleteResults([]);
      setAutocompleteActive(true);
    }
  }, [focusedField, pickupLocation, dropoffLocation, stops]);

  useEffect(() => {
    if (
      autocompleteServiceRef.current &&
      autocompleteInput &&
      (["pickup", "dropoff"].includes(focusedField) ||
        (focusedField && focusedField.startsWith("stop-")))
    ) {
      autocompleteServiceRef.current.getPlacePredictions(
        { input: autocompleteInput, componentRestrictions: { country: "au" } },
        (predictions, status) => {
          if (
            status === window.google.maps.places.PlacesServiceStatus.OK &&
            predictions
          ) {
            setAutocompleteResults(predictions);
          } else {
            setAutocompleteResults([]);
          }
        }
      );
    } else {
      setAutocompleteResults([]);
    }
  }, [autocompleteInput, focusedField]);

  const handleSelectAutocomplete = (desc) => {
    if (focusedField === "pickup") {
      setPickupLocation(desc);
    } else if (focusedField === "dropoff") {
      setDropoffLocation(desc);
    } else if (focusedField && focusedField.startsWith("stop-")) {
      const idx = parseInt(focusedField.replace("stop-", ""));
      setStops(stops.map((s, i) => (i === idx ? desc : s)));
    }
    setAutocompleteActive(false);
    setAutocompleteInput("");
    setFocusedField(null);
    setAutocompleteResults([]);
  };

  const handleOverlayInputChange = (val) => {
    setAutocompleteInput(val);
  };

  useEffect(() => {
    const fetchStopFee = async () => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/get-stop-fee`
        );
        setStopFee(response.data.stopFee);
      } catch {}
    };
    fetchStopFee();
  }, []);

  useEffect(() => {
    if (isLoaded && mapLoaded && pickupLocation && dropoffLocation) {
      const waypoints = stops.filter(Boolean).map((stop) => ({
        location: stop,
        stopover: true,
      }));
      const directionsService = new window.google.maps.DirectionsService();
      if (!directionsRendererRef.current) {
        directionsRendererRef.current =
          new window.google.maps.DirectionsRenderer({
            suppressMarkers: false,
            polylineOptions: {
              strokeColor: "#FB4A46",
              strokeOpacity: 0.8,
              strokeWeight: 4,
            },
          });
      }
      directionsRendererRef.current.setMap(mapRef.current);
      directionsService.route(
        {
          origin: pickupLocation,
          destination: dropoffLocation,
          waypoints,
          travelMode: window.google.maps.TravelMode.DRIVING,
        },
        (response, status) => {
          if (status === "OK") {
            directionsRendererRef.current.setDirections(response);
          }
        }
      );
    }
  }, [pickupLocation, dropoffLocation, stops, isLoaded, mapLoaded]);

  const handleAddStop = () => {
    if (stops.length < MAX_STOPS) setStops([...stops, ""]);
  };
  const handleRemoveStop = (idx) =>
    setStops(stops.filter((_, i) => i !== idx));
  const handleStopChange = (idx, val) =>
    setStops(stops.map((s, i) => (i === idx ? val : s)));

  const openFieldOverlay = (field) => setFocusedField(field);
  const closeFieldOverlay = () => {
    setFocusedField(null);
    setAutocompleteActive(false);
    setAutocompleteResults([]);
    setAutocompleteInput("");
  };

  const openDateTimePicker = () => {
    setFocusedField("datetime");
    setShowTimePicker(false);
  };

  const selectTimeSlot = (val) => {
    setTime(val);
    setShowTimePicker(false);
    setFocusedField(null);
  };

  const geocodeAddress = async (address) => {
    return new Promise((resolve) => {
      const geocoder = new window.google.maps.Geocoder();
      geocoder.geocode({ address }, (results, status) => {
        if (status === "OK" && results[0]) {
          const location = results[0].geometry.location;
          resolve({ lat: location.lat(), lng: location.lng() });
        } else {
          resolve(null);
        }
      });
    });
  };

  const validateLocations = async (
    pickupLat,
    pickupLon,
    dropoffLat,
    dropoffLon
  ) => {
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/validate-locations`,
        { pickupLat, pickupLon, dropoffLat, dropoffLon }
      );
      const { pickupInZone, dropoffInZone } = response.data;
      if (!pickupInZone && !dropoffInZone) {
        window.alert(
          "Both locations are outside our operating zones. Please contact us for a quote."
        );
        return false;
      }
      return true;
    } catch (error) {
      window.alert("Error validating locations. Please try again.");
      return false;
    }
  };

  const checkPausedDate = async (pickupDate) => {
    try {
      const formattedDate = getLocalYMD(pickupDate);
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/check-paused-date`,
        { pickupDate: formattedDate }
      );
      if (response.data.isPaused) {
        window.alert(
          `Bookings are paused on ${formattedDate}. ${
            response.data.reason || "Please contact us for a quote."
          }`
        );
        return true;
      } else {
        return false;
      }
    } catch (error) {
      window.alert("Error checking paused date. Please try again.");
      return false;
    }
  };

  const validatePickupTime = async (pickupDate, pickupTime) => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/get-prior-booking-settings`
      );
      const { minHoursBeforeBooking, instantBookingMessage } = response.data;
      const pickupDateTime = new Date(`${getLocalYMD(pickupDate)}T${toHM(pickupTime)}`);
      const now = new Date();
      const diffInHours = (pickupDateTime - now) / (1000 * 60 * 60);
      if (diffInHours < minHoursBeforeBooking) {
        window.alert(instantBookingMessage);
        return false;
      }
      return true;
    } catch (error) {
      window.alert("Error validating pickup time. Please try again.");
      return false;
    }
  };

  const onSubmit = async () => {
    setLocalLoading(true);

    try {
      if (!date || !time) {
        window.alert("Please select both pick-up date and time.");
        setLocalLoading(false);
        return;
      }

      const response = await axios.post(`${process.env.REACT_APP_API_URL}/get-distance`, {
        origin: pickupLocation,
        destination: dropoffLocation,
        stops: stops,
      });

      if (response.status === 429) {
        throw new Error(response.data.error);
      }

      const pickupDate = getLocalYMD(date);
      const pickupTime = toHM(time);

      const isPaused = await checkPausedDate(pickupDate);
      if (isPaused) {
        setLocalLoading(false);
        return;
      }

      const isValidTime = await validatePickupTime(pickupDate, pickupTime);
      if (!isValidTime) {
        setLocalLoading(false);
        return;
      }

      const pickupCoords = await geocodeAddress(pickupLocation);
      const dropoffCoords = await geocodeAddress(dropoffLocation);
      if (!pickupCoords || !dropoffCoords) {
        window.alert("Invalid pickup or dropoff location.");
        setLocalLoading(false);
        return;
      }

      const isValid = await validateLocations(
        pickupCoords.lat,
        pickupCoords.lng,
        dropoffCoords.lat,
        dropoffCoords.lng
      );
      if (!isValid) {
        setLocalLoading(false);
        return;
      }

      setLocalLoading(false);

      if (isReturnBooking) {
        context.handleReturnDistanceCalculated(
          pickupLocation,
          dropoffLocation,
          stops,
          pickupDate,
          pickupTime,
          pickupCoords.lat,
          pickupCoords.lng,
          dropoffCoords.lat,
          dropoffCoords.lng
        );
      } else {
        context.handleOutboundDistanceCalculated(
          pickupLocation,
          dropoffLocation,
          stops,
          pickupDate,
          pickupTime,
          pickupCoords.lat,
          pickupCoords.lng,
          dropoffCoords.lat,
          dropoffCoords.lng
        );
      }
    } catch (error) {
      setLocalLoading(false);
      if (error.response?.status === 429) {
        window.alert(error.response.data.error);
      } else {
        window.alert("Something went wrong. Please try again.");
      }
    }
  };

  const showOverlay = focusedField !== null;
  const sheetClasses = [
    "location-form-sheet",
    showOverlay ? "expanded focused-mode" : "",
  ].join(" ");

  const renderOverlayField = () => {
    if (
      focusedField === "pickup" ||
      focusedField === "dropoff" ||
      (focusedField && focusedField.startsWith("stop-"))
    ) {
      let labelIcon = "fa-location-dot";
      let placeholder = "";
      let value = autocompleteInput;
      if (focusedField === "pickup") {
        labelIcon = "fa-circle-dot";
        placeholder = "Pick-up Location";
      } else if (focusedField === "dropoff") {
        labelIcon = "fa-location-dot";
        placeholder = "Drop-off Location";
      } else if (focusedField && focusedField.startsWith("stop-")) {
        labelIcon = "fa-map-pin";
        const idx = parseInt(focusedField.replace("stop-", ""));
        placeholder = `Stop Location ${idx + 1}`;
      }
      return (
        <div
          className={
            focusedField && focusedField.startsWith("stop-")
              ? "stop-active-container"
              : "active-input-container"
          }
          style={{ position: "relative" }}
        >
          <button
            type="button"
            className="back-button"
            onClick={closeFieldOverlay}
          >
            <i className="fa fa-arrow-left"></i>
          </button>
          <div className="input-group flex-grow-1">
            <span className="input-group-text input-icon">
              <i className={`fa ${labelIcon}`} style={{ color: "#222B45" }}></i>
            </span>
            <input
              type="text"
              className="form-control"
              placeholder={placeholder}
              value={value}
              onChange={(e) => handleOverlayInputChange(e.target.value)}
              autoFocus
              required
            />
          </div>
          {autocompleteActive && autocompleteResults.length > 0 && (
            <div
              className="autocomplete-overlay"
              style={{
                position: "absolute",
                top: 63,
                left: 0,
                right: 0,
                zIndex: 999,
              }}
            >
              <div className="autocomplete-list">
                {autocompleteResults.map((pred) => (
                  <div
                    className="autocomplete-item"
                    key={pred.place_id}
                    onClick={() => handleSelectAutocomplete(pred.description)}
                    tabIndex={0}
                  >
                    <div className="autocomplete-icon">
                      <i
                        className={`fa ${getIconForPlaceType(
                          pred.structured_formatting.main_text,
                          pred.structured_formatting.secondary_text
                        )}`}
                      ></i>
                    </div>
                    <div>
                      <div className="autocomplete-main">
                        {pred.structured_formatting.main_text}
                      </div>
                      <div className="autocomplete-address">
                        {pred.structured_formatting.secondary_text}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      );
    }
    return null;
  };

  return (
    <div style={{ background: "none", boxShadow: "none" }}>
      <div className="map-container">
        <div
          className={`map-area position-relative${
            showOverlay ? " sheet-hidden" : ""
          }`}
          id="mapArea"
        >
          {isLoaded && (
            <div
              id="map"
              ref={(ref) => {
                if (ref && !mapLoaded) {
                  mapRef.current = new window.google.maps.Map(ref, {
                    zoom: 10,
                    center: { lat: -27.4698, lng: 153.0251 },
                    gestureHandling: "none",
                    zoomControl: false,
                    disableDoubleClickZoom: true,
                    draggable: false,
                    scrollwheel: false,
                    disableDefaultUI: true,
                    keyboardShortcuts: false,
                  });
                  setMapLoaded(true);
                }
              }}
              style={{
                height: 340,
                width: "100%",
                maxWidth: 430,
                margin: "auto",
                borderRadius: 0,
              }}
            />
          )}
        </div>
      </div>
      <div className={sheetClasses} style={{ maxWidth: 430, margin: "0 auto" }}>
        <div className="sheet-header">
          <span className="sheet-header-bar"></span>
          <div className="sheet-title mt-2 mb-2">
            {isReturnBooking
              ? "Enter Return Trip Details"
              : "Plan Your Luxury Ride"}
          </div>
        </div>
        <form
          className="form-content"
          autoComplete="off"
          style={{ position: "relative", height: "100%" }}
          onSubmit={(e) => {
            e.preventDefault();
            onSubmit();
          }}
        >
          {showOverlay && renderOverlayField()}

          {focusedField === "datetime" && (
            <div className="datetime-container">
              <button
                type="button"
                className="back-button"
                onClick={closeFieldOverlay}
              >
                <i className="fa fa-arrow-left"></i>
              </button>
              <div
                className="input-group flex-grow-1"
                onClick={() => setShowTimePicker(false)}
              >
                <span className="input-group-text input-icon">
                  <i
                    className="fa fa-calendar-days"
                    style={{ color: "#222B45" }}
                  ></i>
                </span>
                <Flatpickr
                  value={date}
                  onChange={(dateArr) => {
                    setDate(getLocalYMD(dateArr[0]));
                    setShowTimePicker(true);
                  }}
                  options={{
                    minDate: "today",
                    inline: true,
                    dateFormat: "Y-m-d",
                  }}
                  className="form-control"
                  style={{
                    border: "none",
                    boxShadow: "none",
                    background: "transparent",
                  }}
                />
              </div>
            </div>
          )}
          {showTimePicker && (
            <div className="time-picker" style={{ display: "block" }}>
              <div className="datetime-header">
                <button
                  type="button"
                  className="datetime-nav-btn"
                  onClick={() => setShowTimePicker(false)}
                >
                  <i className="fa fa-arrow-left"></i>
                </button>
                <div className="datetime-title">Select Time</div>
                <div style={{ width: 24 }}></div>
              </div>
              <div
                className="time-slots"
                style={{
                  maxHeight: 260,
                  overflowY: "auto",
                  padding: "0 16px",
                }}
              >
                {timeSlots.map((opt) => (
                  <div
                    className={`time-slot${time === opt.raw ? " selected" : ""}`}
                    key={opt.raw}
                    onClick={() => selectTimeSlot(opt.raw)}
                  >
                    {opt.label}
                  </div>
                ))}
              </div>
            </div>
          )}
          {!showOverlay && (
            <>
              <div className="input-group mb-2" onClick={() => openFieldOverlay("pickup")}>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Pick-up Location"
                  value={pickupLocation}
                  readOnly
                  required
                />
                {pickupLocation && (
                  <span
                    className="text-primary ms-2 bg-info"
                    style={{
                      cursor: 'pointer',
                      position: 'absolute',
                      borderRadius: '5px',
                      padding: '0px 4px',
                      color: '#f8f9fa !important',
                      fontSize: '13px',
                      right: '10px',
                      top: '12px'
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      setPickupLocation("");
                    }}
                  >
                    CLEAR
                  </span>
                )}
              </div>

              <div className="input-group mb-2" onClick={() => openFieldOverlay("dropoff")}>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Drop-off Location"
                  value={dropoffLocation}
                  readOnly
                  required
                />
                {dropoffLocation && (
                  <span
                    className="text-primary ms-2 bg-info"
                    style={{
                      cursor: 'pointer',
                      position: 'absolute',
                      borderRadius: '5px',
                      padding: '0px 4px',
                      color: '#f8f9fa !important',
                      fontSize: '13px',
                      right: '10px',
                      top: '12px'
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      setDropoffLocation("");
                    }}
                  >
                    CLEAR
                  </span>
                )}
              </div>
              <div
                className="stops-container"
                style={{ display: stops.length > 0 ? "block" : "none" }}
              >
                {stops.map((stop, idx) => (
                  <div
                    className="input-group mb-2"
                    key={idx}
                    onClick={() => openFieldOverlay(`stop-${idx}`)}
                  >
                    <span className="input-group-text input-icon">
                      <i
                        className="fa fa-map-pin"
                        style={{ color: "#222B45" }}
                      ></i>
                    </span>
                    <input
                      type="text"
                      className="form-control"
                      placeholder={`Stop Location ${idx + 1}`}
                      value={stop}
                      readOnly
                    />
                    <button
                      type="button"
                      className="stop-remove-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleRemoveStop(idx);
                      }}
                    >
                      <i className="fa fa-times"></i>
                    </button>
                  </div>
                ))}
              </div>
              <div
                className="d-flex align-items-center mb-2"
                id="addStopsRow"
              >
                <span
                  className="add-stop"
                  style={{
                    cursor:
                      stops.length < MAX_STOPS ? "pointer" : "not-allowed",
                  }}
                  onClick={handleAddStop}
                >
                  <i className="fa fa-plus"></i> Add Stops
                </span>
                {stops.length > 0 && (
                  <span className="ms-2 text-muted">
                    ({stops.length}/{MAX_STOPS})
                  </span>
                )}
              </div>
              <div className="form-controls-row mb-2">
                <button
                  type="button"
                  className="btn btn-light d-flex align-items-center justify-content-center gap-2"
                  style={{ fontWeight: 500 }}
                  onClick={openDateTimePicker}
                >
                  <i
                    className="fa fa-calendar-days"
                    style={{ color: "#222B45" }}
                  ></i>
                  <span>
                    {date && time ? `${date} at ${time}` : "Date & Time"}
                  </span>
                </button>
                <div className="input-group">
                  <span className="input-group-text">
                    <i className="fas fa-user-group"></i>
                  </span>
                  <select
                    className="form-select"
                    value={
                      isReturnBooking
                        ? context.personalDetails.returnNumberOfPassengers
                        : context.personalDetails.numberOfPassengers
                    }
                    onChange={(e) =>
                      context.setPersonalDetails((prev) => ({
                        ...prev,
                        [isReturnBooking
                          ? "returnNumberOfPassengers"
                          : "numberOfPassengers"]: parseInt(e.target.value),
                      }))
                    }
                    required
                  >
                    {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11].map((num) => (
                      <option key={num} value={num}>
                        {num}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="input-group">
                  <span className="input-group-text">
                    <i className="fas fa-suitcase-rolling"></i>
                  </span>
                  <select
                    className="form-select"
                    value={
                      isReturnBooking
                        ? context.personalDetails.returnNumberOfLuggage
                        : context.personalDetails.numberOfLuggage
                    }
                    onChange={(e) =>
                      context.setPersonalDetails((prev) => ({
                        ...prev,
                        [isReturnBooking
                          ? "returnNumberOfLuggage"
                          : "numberOfLuggage"]: parseInt(e.target.value),
                      }))
                    }
                    required
                  >
                    {[0, 1, 2, 3, 4, 5, 6, 7].map((num) => (
                      <option key={num} value={num}>
                        {num}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <input
                type="hidden"
                {...register("pickupDate", { required: true })}
                value={date}
                readOnly
              />
              <input
                type="hidden"
                {...register("pickupTime", { required: true })}
                value={time}
                readOnly
              />
              <button
                type="submit"
                className="btn btn-estimate mt-3"
                id="fareEstimateBtn"
                disabled={localLoading}
              >
                {localLoading ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2"></span>
                    Loading...
                  </>
                ) : (
                  "Fare Estimate"
                )}
              </button>

              {isReturnBooking && (
                <button
                  type="button"
                  onClick={context.handleBackToOneWayBooking}
                  className="btn btn-light mt-2"
                  style={{ width: "100%" }}
                >
                  <i className="fa fa-arrow-left"></i> Back to One-Way Booking
                </button>
              )}
            </>
          )}
        </form>
      </div>
    </div>
  );
};

export default DistanceForm;
